import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Wrench, ArrowClockwise, WarningOctagon, CheckCircle } from "@phosphor-icons/react";

const card = "rounded-2xl border border-amber-200 bg-amber-50/60 p-5 shadow-sm";

// Super Admin: emergency lock/unlock of public online booking + calendar reconciliation report.
export default function BookingMaintenancePanel() {
  const [locked, setLocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [rsc, setRsc] = useState(null); // reservation id being rescheduled
  const [rTgl, setRTgl] = useState("");
  const [rJam, setRJam] = useState("");
  const [rBusy, setRBusy] = useState(false);

  const doReschedule = async (id) => {
    if (!rTgl || !rJam) { toast.error("Isi tanggal & jam baru"); return; }
    setRBusy(true);
    try {
      await api.patch(`/reservations/${id}/edit`, { tanggal_kunjungan: rTgl, jam_kunjungan: rJam, alasan_perubahan: "Perbaikan double-booking (rekonsiliasi)", override: false });
      toast.success("Jadwal berhasil diubah");
      setRsc(null); setRTgl(""); setRJam("");
      await runReport();
    } catch (e) { toast.error(apiError(e)); } finally { setRBusy(false); }
  };

  useEffect(() => {
    api.get("/public/settings").then(({ data }) => setLocked(!!data.booking_locked)).catch(() => {});
  }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/booking-lock", { locked: next });
      setLocked(data.booking_locked);
      toast.success(data.booking_locked ? "Reservasi online DIKUNCI" : "Reservasi online DIBUKA");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const runReport = async () => {
    setLoadingReport(true);
    try {
      const { data } = await api.get("/admin/reconcile-calendar");
      setReport(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoadingReport(false); }
  };

  return (
    <div className={card} data-testid="booking-maintenance-panel">
      <div className="mb-3 flex items-center gap-2">
        <Wrench size={18} weight="duotone" className="text-amber-600" />
        <h3 className="font-heading font-bold text-slate-800">Pemeliharaan Reservasi Online</h3>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">Status Reservasi Online Publik</p>
          <p className={`text-sm font-bold ${locked ? "text-rose-600" : "text-emerald-600"}`} data-testid="lock-status">
            {locked === null ? "..." : locked ? "🔒 TERKUNCI (pemeliharaan)" : "🟢 TERBUKA"}
          </p>
        </div>
        {locked ? (
          <button data-testid="unlock-booking" disabled={busy} onClick={() => toggle(false)}
            className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
            Buka Reservasi Online
          </button>
        ) : (
          <button data-testid="lock-booking" disabled={busy} onClick={() => toggle(true)}
            className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60">
            Kunci Reservasi Online
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-500">Saat terkunci, pasien tidak bisa membuat reservasi online (backend menolak). Admin tetap bisa membuat reservasi manual.</p>

      <div className="mt-4 border-t border-amber-200 pt-4">
        <button data-testid="run-reconcile" disabled={loadingReport} onClick={runReport}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-900 disabled:opacity-60">
          <ArrowClockwise size={16} weight="bold" className={loadingReport ? "animate-spin" : ""} /> Jalankan Rekonsiliasi Kalender
        </button>

        {report && (
          <div className="mt-3 space-y-2 rounded-xl bg-white p-3 text-sm" data-testid="reconcile-report">
            <Row label="Total reservasi mendatang" value={report.upcoming_reservations} />
            <Row label="Berhasil dipetakan ke kalender" value={report.mapped_to_calendar} />
            <Row label="Reservasi tanpa petugas" value={report.reservations_without_staff} warn={report.reservations_without_staff > 0} />
            <Row label="Manual block mendatang" value={report.manual_blocks_upcoming} />
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 font-bold ${report.double_booking_conflicts_found > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
              {report.double_booking_conflicts_found > 0 ? <WarningOctagon size={18} weight="fill" /> : <CheckCircle size={18} weight="fill" />}
              {report.double_booking_conflicts_found > 0 ? `${report.double_booking_conflicts_found} DOUBLE-BOOKING ditemukan` : "Tidak ada double-booking 🎉"}
            </div>
            {report.conflicts?.length > 0 && (
              <div className="space-y-1.5">
                {report.conflicts.map((c, i) => (
                  <div key={i} className="rounded-lg border border-rose-200 bg-rose-50/50 px-3 py-2 text-xs text-slate-700" data-testid={`conflict-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span><span className="font-bold text-rose-700">{c.petugas}</span> · {c.tanggal}: <b>{c.a.jam}</b> ({c.a.nama}) ⨯ <b>{c.b.jam}</b> ({c.b.nama})</span>
                      <button data-testid={`reschedule-${i}`} onClick={() => { setRsc(rsc === c.b.id ? null : c.b.id); setRTgl(c.tanggal); setRJam(""); }}
                        className="rounded-lg bg-[#7C4DE0] px-2.5 py-1 text-[11px] font-bold text-white hover:bg-[#6A38C7]">Reschedule {c.b.nama}</button>
                    </div>
                    {rsc === c.b.id && (
                      <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-white p-2" data-testid={`reschedule-form-${i}`}>
                        <div><label className="block text-[10px] font-semibold text-slate-400">Tanggal baru</label><input data-testid={`resc-date-${i}`} type="date" value={rTgl} onChange={(e) => setRTgl(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs" /></div>
                        <div><label className="block text-[10px] font-semibold text-slate-400">Jam baru</label><input data-testid={`resc-time-${i}`} type="time" value={rJam} onChange={(e) => setRJam(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs" /></div>
                        <button data-testid={`resc-save-${i}`} disabled={rBusy} onClick={() => doReschedule(c.b.id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700 disabled:opacity-60">Simpan</button>
                        <button onClick={() => setRsc(null)} className="rounded-lg px-2 py-1.5 text-[11px] font-semibold text-slate-400">Batal</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {report.reservations_without_staff_list?.length > 0 && (
              <div className="space-y-1 pt-1">
                <p className="text-xs font-bold text-amber-700">Tanpa petugas:</p>
                {report.reservations_without_staff_list.map((r, i) => (
                  <div key={i} className="text-xs text-slate-600">{r.tanggal} {r.jam} · {r.nama} · {r.layanan}</div>
                ))}
              </div>
            )}
            <p className="pt-1 text-[11px] text-slate-400">Selesaikan semua konflik (reschedule salah satu) sebelum membuka reservasi online kembali.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, warn }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-bold ${warn ? "text-amber-600" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}
